/**
 * The verdict on one example, judged from its MusicXML at the moment the page is opened.
 *
 * Faults are printed as the predicate wrote them, because each one already names the bar, the
 * part and the table row it was measured against — a tidier summary would drop exactly the
 * detail a reader needs to go and check it. Faults the book itself marked approximate are
 * counted separately: those are the book departing from its own tables, not a misreading.
 */

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PREDICATES, type Judgement } from "@app/shared-types";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import { RuleBar, VerdictPill } from "../components/marks";
import Board from "../components/Board";
import { ACTION_LIVE } from "../components/Chrome";
import { Failed, Kicker, Loading } from "../components/Page";
import { keyName, rate, semitones } from "../lib/format";

const SHOWN = 2;

/** The predicate prefixes its lines with the example id; the heading already says which. */
function trimmed(fault: string, id: string): string {
  return fault.startsWith(`${id} `) ? fault.slice(id.length + 1) : fault;
}

function Faults({ judged, id }: { judged: Judgement; id: string }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (judged.faults.length === 0) return null;
  const shown = open ? judged.faults : judged.faults.slice(0, SHOWN);
  return (
    <div className="space-y-2 border-t border-rule-faint px-4 py-3">
      <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-fault-strong">
        {judged.predicate}
      </div>
      <ul className="space-y-1.5">
        {shown.map((fault, index) => (
          <li key={index} className="font-mono text-xs leading-relaxed text-ink-faded">
            {trimmed(fault, id)}
          </li>
        ))}
      </ul>
      {judged.faults.length > SHOWN ? (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="min-h-[44px] text-sm font-medium text-clean hover:text-clean-strong"
        >
          {open ? "Show fewer" : `Show the other ${judged.faults.length - SHOWN}`}
        </button>
      ) : null}
    </div>
  );
}

export default function VerdictPage(): JSX.Element {
  const { id = "" } = useParams();
  const { data, error, loading } = useAsync(`example:${id}`, () => api.example(id));

  const shell = (children: JSX.Element): JSX.Element => (
    <Board kicker={`Example ${id}`} title={id} back={{ to: "/book", label: "The book" }}>
      {children}
    </Board>
  );
  if (loading) return shell(<Loading what={`example ${id}`} />);
  if (error !== null) return shell(<Failed error={error} />);
  if (data === null) return shell(<Failed error="that example came back empty" />);

  const { verdict, score: meta, entry } = data;
  const byName = new Map(verdict.judgements.map((one) => [one.predicate, one]));
  const approximate = verdict.judgements
    .flatMap((one) => one.faults)
    .filter((fault) => fault.includes("approx. chart row"));

  return (
    <Board
      kicker={`Example ${data.id}`}
      title={entry?.caption ?? meta.title ?? data.id}
      back={{ to: "/book", label: "The book" }}
      actions={
        data.comparison ? (
          <Link to={`/book/${data.id}/compare`} className={ACTION_LIVE}>
            Re-orchestrate and compare
          </Link>
        ) : undefined
      }
    >
      <div className="space-y-7">
        <div className="flex flex-wrap items-baseline gap-3">
          <VerdictPill faults={verdict.faultCount} />
          <span className="font-mono text-[34px] leading-none">{rate(verdict.score)}</span>
          <span className="text-xs text-ink-faded">
            overall · coverage {rate(verdict.coverage)} · quality {semitones(verdict.quality, 3)}
          </span>
        </div>

        <p className="text-sm text-ink-faded">
          {[
            meta.timeSignature,
            keyName(meta.keyFifths),
            `${meta.measures} bar${meta.measures === 1 ? "" : "s"}`,
            meta.families.join(", "),
          ].join(" · ")}
        </p>

        {entry?.techniques?.length ? (
          <p className="font-display text-lg italic leading-snug text-ink-faded">
            {entry.techniques.join(" ")}
          </p>
        ) : null}

        <section className="space-y-2">
          <Kicker>Eight rules</Kicker>
          <div className="rounded-xl border border-rule bg-leaf px-4">
            {PREDICATES.map((name) => {
              const judged = byName.get(name);
              return (
                <RuleBar
                  key={name}
                  name={name}
                  score={judged?.score ?? null}
                  detail={judged && judged.judged > 0 ? `${judged.judged}` : "silent"}
                />
              );
            })}
          </div>
          {verdict.silent.length > 0 ? (
            <p className="text-xs leading-relaxed text-ink-faded">
              {verdict.silent.length} of {PREDICATES.length} judged nothing here and score nothing —
              silence is not approval, so they are left out of the average rather than counted as
              passes.
            </p>
          ) : null}
        </section>

        {verdict.faultCount > 0 ? (
          <section className="space-y-2">
            <Kicker>What it caught</Kicker>
            <div className="overflow-hidden rounded-xl border border-rule bg-leaf">
              {verdict.judgements
                .filter((one) => one.faults.length > 0)
                .map((one) => (
                  <Faults key={one.predicate} judged={one} id={data.id} />
                ))}
            </div>
          </section>
        ) : null}

        {approximate.length > 0 ? (
          <section className="rounded-xl border border-departure bg-departure/40 p-4">
            <Kicker>Known departure</Kicker>
            <p className="mt-2 text-sm leading-relaxed text-departure-deep">
              {approximate.length} of these faults land on a chart row the book prints as
              approximate. They are the book departing from its own tables, not a misreading of the
              score.
            </p>
          </section>
        ) : null}

        {data.comparison ? null : (
          <p className="text-xs leading-relaxed text-ink-faded">
            This example is not a brief the generator can be handed — there is no bare harmony under
            it to re-voice, so there is nothing to compare against.
          </p>
        )}
      </div>
    </Board>
  );
}
